import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import JunagoLogo from "../components/JunagoLogo";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/auth/reset-password`, { token, password: form.password });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/login?context=vendor"), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Une erreur est survenue, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-sm w-full">
        <Link to="/" className="inline-block mb-10"><JunagoLogo size={34} tone="dark" /></Link>
        {done ? (
          <div data-testid="reset-success">
            <div className="h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="mt-5 font-heading text-2xl font-medium tracking-tight text-slate-900">Mot de passe mis à jour</h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">Redirection vers la connexion…</p>
            <Link to="/login" className="mt-6 inline-flex text-sm font-semibold text-violet-600 hover:text-violet-800" data-testid="reset-go-login">
              Se connecter maintenant
            </Link>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token">
            <h1 className="font-heading text-2xl font-medium tracking-tight text-slate-900">Lien invalide</h1>
            <p className="mt-3 text-sm text-slate-600">Ce lien de réinitialisation est incomplet ou expiré.</p>
            <Link to="/mot-de-passe-oublie" className="mt-6 inline-flex text-sm font-semibold text-violet-600 hover:text-violet-800">
              Refaire une demande
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-900" data-testid="reset-title">
              Nouveau mot de passe
            </h1>
            <p className="mt-2 text-sm text-slate-500">Choisissez un mot de passe d'au moins 8 caractères.</p>
            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-form">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Nouveau mot de passe</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="8 caractères minimum" data-testid="reset-password" />
                </div>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Confirmez le mot de passe</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="Répétez le mot de passe" data-testid="reset-confirm" />
                </div>
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={loading} className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full btn-primary font-semibold text-sm disabled:opacity-50" data-testid="reset-submit">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Mettre à jour le mot de passe
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
